import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { tl } from "@/lib/i18nFormat";
import { PageHeader, LoadingBlock, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Users2, MapPin } from "lucide-react";

import { useI18n } from "@/context/I18nContext";

// E2 cross-store labor pool (frontline claim) + manager approvals, plus E3 PTO balance card.
export default function LaborPool() {
  return (
    <div data-testid="labor-pool-page">
      <PageHeader title="Labor Pool & Balance" subtitle="Pick up eligible shifts at nearby stores and see your policy-derived PTO balance" testId="labor-pool-header" />
      <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
        <div className="space-y-4"><PoolOpen /><ManagerClaims /></div>
        <PtoBalance />
      </div>
    </div>
  );
}

const PoolOpen: React.FC = () => {
  const {
    t
  } = useI18n();

  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const load = () => api.get("/scheduling/pool/open-shifts").then((r) => setRows(r.data.pool_open_shifts || [])).catch(() => setRows([])).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);
  const claim = async (id: string) => {
    try { await api.post(`/scheduling/pool/shifts/${id}/claim`, {}); toast.success("Claim submitted — awaiting manager approval"); load(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Not eligible"); }
  };
  if (loading) return <LoadingBlock />;
  return (
    (<Card className="p-4" data-testid="pool-open">
      <div className="mb-2 flex items-center gap-2 font-medium"><MapPin size={16} className="text-brand" />{" "}{t("Open shifts near you")}</div>
      {rows.length === 0 ? <EmptyState icon={<Users2 size={22} />} title="No pool shifts" description="Eligible open shifts at nearby stores will appear here." /> :
        <div className="space-y-2">
          {rows.map((s) => (
            <div key={s.id} className="flex items-center justify-between rounded-md border border-border p-3" data-testid={`pool-shift-${s.id}`}>
              <div><div className="font-medium">{t("Store")}{" "}{s.store_number}· {s.date}</div><div className="text-xs text-muted-foreground">{s.start}–{s.end}{s.role ? ` · ${s.role}` : ""} <Badge variant="secondary" className="ml-1">{t("borrow")}</Badge></div></div>
              <Button size="sm" disabled={s.my_pending_claim} onClick={() => claim(s.id)} data-testid={`pool-claim-${s.id}`}>{s.my_pending_claim ? t("Claimed") : t("Claim")}</Button>
            </div>
          ))}
        </div>}
    </Card>)
  );
};

const ManagerClaims: React.FC = () => {
  const {
    t
  } = useI18n();

  const [rows, setRows] = useState<any[]>([]);
  const [show, setShow] = useState(true);
  const load = () => api.get("/scheduling/pool/claims", { params: { status: "pending" } }).then((r) => setRows(r.data.pool_claims || [])).catch(() => setShow(false));
  useEffect(() => { load(); }, []);
  const decide = async (id: string, approve: boolean) => {
    try { await api.post(`/scheduling/pool/claims/${id}/decision`, { approve, note: approve ? null : "not approved" }); toast.success(approve ? tl("Approved") : tl("Denied")); load(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  if (!show) return null;
  return (
    (<Card className="p-4" data-testid="pool-claims">
      <div className="mb-2 font-medium">{t("Cross-store claims to approve")}</div>
      {rows.length === 0 ? <EmptyState icon={<Users2 size={22} />} title="No pending claims" /> :
        <div className="space-y-2">{rows.map((c) => (
          <div key={c.id} className="flex items-center justify-between rounded-md border border-border p-3" data-testid={`pool-claim-row-${c.id}`}>
            <div><div className="font-medium">{c.claimant_name} <span className="text-xs text-muted-foreground">{t("(home")}{" "}{c.home_store_number || "—"})</span></div><div className="text-xs text-muted-foreground">{c.shift ? `${c.shift.date} ${c.shift.start}–${c.shift.end}` : ""}</div></div>
            <div className="flex gap-1.5"><Button size="sm" onClick={() => decide(c.id, true)} data-testid={`pool-approve-${c.id}`}>{t("Approve")}</Button><Button size="sm" variant="ghost" onClick={() => decide(c.id, false)} data-testid={`pool-deny-${c.id}`}>{t("Deny")}</Button></div>
          </div>
        ))}</div>}
    </Card>)
  );
};

const PtoBalance: React.FC = () => {
  const {
    t
  } = useI18n();

  const [d, setD] = useState<any>(null);
  useEffect(() => { api.get("/scheduling/pto/balance").then((r) => setD(r.data)).catch(() => setD({ error: true })); }, []);
  if (!d) return <LoadingBlock />;
  return (
    (<Card className="h-fit p-4" data-testid="pto-balance">
      <div className="mb-2 font-medium">{t("PTO balance")}</div>
      {!d.configured ? (
        <div data-testid="pto-not-configured"><Badge variant="secondary">{t("Not configured")}</Badge><p className="mt-2 text-sm text-muted-foreground">{d.message || "No PTO policy is configured."}</p></div>
      ) : (
        <div className="space-y-1.5">
          <div className="text-3xl font-semibold" data-testid="pto-hours">{d.balance_hours}h</div>
          <div className="text-xs text-muted-foreground">{t("Accrued")}{" "}{d.accrued_hours}{t("h − used est.")}{" "}{d.used_hours_estimate}h</div>
          <Badge variant="outline" className="mt-1">{t("policy-derived")}</Badge>
          <p className="mt-2 text-xs text-muted-foreground" data-testid="pto-disclaimer">{d.disclaimer}</p>
        </div>
      )}
    </Card>)
  );
};
